import { useEffect, useState, useRef, useCallback } from "react";
import { tr } from "../i18n/tr";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import PageHeader from "../components/PageHeader";
import { ListSkeleton } from "../components/Skeletons";
import {
  GraduationCap, CheckCircle2, PlayCircle, Lock, FileSignature,
  Loader2, BookOpen, ClipboardList, Shield, ChevronRight, X,
} from "lucide-react";
import { toast } from "sonner";
import { ScormPlayer } from "../components/lms/ScormPlayer";
// Hard-blocks content access when mandatory Qualiopi documents are unsigned.

export default function StudentPortalPage() {
  const { axiosAuth, user } = useAuth();
  const api = axiosAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/lms/student/my-enrollments");
      setEnrollments(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="student-portal">
      <PageHeader
        icon={<GraduationCap />}
        title={tr("lms.mes_formations_375f", "Mes Formations")}
        description={`Bonjour ${user?.full_name || user?.email} — vos sessions et contenus e-learning`}
        testId="student-portal-header"
      />

      {loading ? <ListSkeleton items={4} testId="student-portal-loading-skeleton" /> :
        enrollments.length === 0 ? (
          <div className="text-center py-20 text-slate-500 border border-dashed border-white/10 rounded-xl">
            <BookOpen className="w-12 h-12 mx-auto text-slate-700 mb-3" />
            {tr("lms.vous_n_etes_inscrit_0415", "Vous n'etes inscrit a aucune formation pour le moment.")}
          </div>
        ) : (
          <div className="grid gap-3">
            {enrollments.map(e => (
              <button key={e.enrollment_id} onClick={() => setSelected(e)}
                data-testid={`student-enrollment-${e.enrollment_id}`}
                className="text-left bg-[#14101c] border border-white/10 rounded-xl p-5 hover:border-[#f7c935]/40 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <GraduationCap className="w-4 h-4 text-[#f7c935]" />
                      <span className="font-bold">{e.program?.title || e.session?.title}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-slate-400">{e.session?.modality}</span>
                    </div>
                    <div className="text-xs text-slate-400">{e.session?.start_date} → {e.session?.end_date}</div>
                    <div className="mt-2 w-full bg-white/5 rounded-full h-2">
                      <div className="bg-[#f7c935] h-2 rounded-full" style={{ width: `${e.progress || 0}%` }} />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">Progression: {e.progress || 0}% · {e.status}</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </button>
            ))}
          </div>
        )}

      <StudentSessionDetail enrollment={selected} onClose={() => { setSelected(null); load(); }} api={api} />
    </div>
  );
}

// ─── Session Detail (content + hard-block) ─────────────────
function StudentSessionDetail({ enrollment, onClose, api }) {
  const [detail, setDetail] = useState(null);
  const [activeSeq, setActiveSeq] = useState(null);
  const [blockDoc, setBlockDoc] = useState(null);

  const load = useCallback(async () => {
    if (!enrollment) return;
    try {
      const r = await api.get(`/lms/student/enrollments/${enrollment.enrollment_id}`);
      setDetail(r.data);
      // Auto-open blocking document if any
      if (r.data?.blocking_documents?.length > 0) {
        setBlockDoc(r.data.blocking_documents[0]);
      }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  // eslint-disable-next-line
  }, [enrollment?.enrollment_id]);

  useEffect(() => { if (enrollment) load(); /* eslint-disable-next-line */ }, [enrollment]);

  if (!enrollment) return null;

  const completed = new Set(detail?.enrollment?.completed_sequence_ids || []);
  const isBlocked = (detail?.blocking_documents?.length || 0) > 0;
  const ls = detail?.learner_space_config || {};

  return (
    <Dialog open={!!enrollment} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl bg-[#0b0811] border border-white/10 text-white" data-testid="student-session-detail">
        <DialogHeader>
          <DialogTitle>{ls.display_name || detail?.program?.title || "..."}</DialogTitle>
        </DialogHeader>
        {!detail ? <Loader2 className="w-6 h-6 animate-spin text-[#f7c935] mx-auto my-8" /> : (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            {ls.description && (
              <div className="bg-[#14101c] border border-white/10 rounded-lg p-4 text-sm text-slate-300 whitespace-pre-wrap">{ls.description}</div>
            )}
            {ls.show_location && detail.session?.location && (
              <div className="text-sm text-slate-400"><b>{tr("lms.lieu_ca42", "Lieu :")}</b> {detail.session.location}</div>
            )}
            {ls.show_planning && (
              <div className="text-sm text-slate-400"><b>{tr("lms.planning_dd98", "Planning :")}</b> {detail.session?.start_date} → {detail.session?.end_date}</div>
            )}
            {isBlocked && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3" data-testid="student-block-banner">
                <Lock className="w-5 h-5 text-red-400 mt-0.5" />
                <div className="flex-1">
                  <div className="font-bold text-red-400">Acces bloque : documents a signer</div>
                  <div className="text-xs text-slate-400 mt-1">Vous devez signer {detail.blocking_documents.length} document(s) obligatoire(s) pour acceder aux contenus.</div>
                  <Button size="sm" onClick={() => setBlockDoc(detail.blocking_documents[0])} className="mt-2 bg-red-500 hover:bg-red-600 text-white" data-testid="sign-required-docs-btn">
                    <FileSignature className="w-4 h-4 mr-2" /> {tr("lms.signer_maintenant_8bc3", "Signer maintenant")}
                  </Button>
                </div>
              </div>
            )}

            <div>
              <h3 className="font-bold mb-2 flex items-center gap-2"><BookOpen className="w-4 h-4" />Sequences ({detail.sequences?.length || 0})</h3>
              <div className="space-y-2">
                {detail.sequences?.map(s => {
                  const done = completed.has(s.sequence_id);
                  return (
                    <button key={s.sequence_id} disabled={isBlocked}
                      onClick={() => setActiveSeq(s)}
                      data-testid={`student-seq-${s.sequence_id}`}
                      className={`w-full text-left bg-[#14101c] border border-white/10 rounded-lg p-3 flex items-center justify-between ${isBlocked ? "opacity-50 cursor-not-allowed" : "hover:border-[#f7c935]/40"}`}>
                      <div className="flex items-center gap-3">
                        {done ? <CheckCircle2 className="w-5 h-5 text-green-400" /> : <PlayCircle className="w-5 h-5 text-slate-500" />}
                        <div>
                          <div className="font-semibold text-sm">{s.title}</div>
                          <div className="text-xs text-slate-500">{s.type} · {s.duration_min} min</div>
                        </div>
                      </div>
                      {isBlocked ? <Lock className="w-4 h-4 text-slate-600" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeSeq && (
          <SequencePlayer
            sequence={activeSeq}
            enrollmentId={enrollment.enrollment_id}
            onClose={() => setActiveSeq(null)}
            onCompleted={() => { setActiveSeq(null); load(); }}
            api={api}
          />
        )}
        {blockDoc && (
          <SignatureDialog
            doc={blockDoc}
            enrollmentId={enrollment.enrollment_id}
            onClose={() => { setBlockDoc(null); load(); }}
            api={api}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Sequence Player ───────────────────────────────────────
function SequencePlayer({ sequence, enrollmentId, onClose, onCompleted, api }) {
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [fileBlob, setFileBlob] = useState(null);

  useEffect(() => {
    if ((sequence.type === "pdf" || sequence.type === "video") && sequence.content_url) {
      api.get(`/lms/sequences/${sequence.sequence_id}/download`).then(r => {
        const bin = atob(r.data.data_b64);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        const blob = new Blob([arr], { type: r.data.mime });
        setFileBlob(URL.createObjectURL(blob));
      }).catch(() => {});
    }
    return () => { if (fileBlob) URL.revokeObjectURL(fileBlob); };
  // eslint-disable-next-line
  }, [sequence.sequence_id]);

  const markDone = async () => {
    try {
      await api.post(`/lms/student/enrollments/${enrollmentId}/sequence/${sequence.sequence_id}/complete`);
      toast.success(tr("lms.sequence_validee_2d41", "Sequence validee"));
      onCompleted();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const submitQuiz = async () => {
    const answers = (sequence.quiz_questions || []).map((_, i) => quizAnswers[i] ?? -1);
    try {
      const r = await api.post(`/lms/student/enrollments/${enrollmentId}/quiz/${sequence.sequence_id}/submit`, { answers });
      setQuizResult(r.data);
      if (r.data.passed) { toast.success(`Reussi ! ${r.data.pct}%`); setTimeout(onCompleted, 1500); }
      else toast.error(`Echec ${r.data.pct}% (minimum ${sequence.quiz_pass_score}%)`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const embedUrl = () => {
    if (sequence.type === "youtube") {
      const m = (sequence.external_url || "").match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      return m ? `https://www.youtube.com/embed/${m[1]}` : null;
    }
    if (sequence.type === "vimeo") {
      const m = (sequence.external_url || "").match(/vimeo\.com\/(\d+)/);
      return m ? `https://player.vimeo.com/video/${m[1]}` : null;
    }
    return null;
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl bg-[#0b0811] border border-white/10 text-white" data-testid="sequence-player">
        <DialogHeader><DialogTitle>{sequence.title}</DialogTitle></DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto pr-2 space-y-3">
          {sequence.type === "text" && (
            <div className="prose prose-invert max-w-none text-sm whitespace-pre-wrap">{sequence.content_text}</div>
          )}
          {(sequence.type === "youtube" || sequence.type === "vimeo") && embedUrl() && (
            <div className="aspect-video">
              <iframe src={embedUrl()} className="w-full h-full rounded" title="video" allowFullScreen />
            </div>
          )}
          {sequence.type === "external" && (
            <a href={sequence.external_url} target="_blank" rel="noreferrer" className="text-[#f7c935] underline">{sequence.external_url}</a>
          )}
          {sequence.type === "pdf" && fileBlob && (
            <iframe src={fileBlob} className="w-full h-[60vh] rounded" title="pdf" />
          )}
          {sequence.type === "video" && fileBlob && (
            <video src={fileBlob} controls className="w-full rounded" />
          )}
          {sequence.type === "scorm" && (
            <ScormPlayer sequence={sequence} enrollmentId={enrollmentId} api={api} onCompleted={onCompleted} />
          )}
          {sequence.type === "quiz" && (
            <div className="space-y-3" data-testid="quiz-form">
              {(sequence.quiz_questions || []).map((q, qi) => (
                <div key={qi} className="bg-[#14101c] border border-white/10 rounded-lg p-3">
                  <div className="font-semibold mb-2">{qi + 1}. {q.question}</div>
                  <div className="space-y-1">
                    {(q.choices || []).map((c, ci) => (
                      <label key={ci} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="radio" name={`q${qi}`} checked={quizAnswers[qi] === ci} onChange={() => setQuizAnswers({ ...quizAnswers, [qi]: ci })} />
                        {c}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {quizResult && (
                <div className={`rounded p-3 ${quizResult.passed ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                  Resultat: {quizResult.score}/{quizResult.max} ({quizResult.pct}%) — {quizResult.passed ? "Reussi" : tr("lms.echec_cd7b", "Echec")}
                </div>
              )}
              <Button onClick={submitQuiz} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="submit-quiz-btn">{tr("lms.valider_mes_reponses_16d8", "Valider mes reponses")}</Button>
            </div>
          )}
        </div>
        {sequence.type !== "quiz" && (
          <div className="flex justify-end mt-3">
            <Button onClick={markDone} className="bg-green-500 hover:bg-green-600 text-white" data-testid="mark-seq-done-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" /> {tr("lms.marquer_comme_termine_79c9", "Marquer comme termine")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Signature Dialog (canvas) ─────────────────────────────
function SignatureDialog({ doc, enrollmentId, onClose, api }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const getXY = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    const clientX = t?.clientX ?? e.clientX;
    const clientY = t?.clientY ?? e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };
  const start = (e) => {
    e.preventDefault(); setDrawing(true); setHasDrawn(true);
    const { x, y } = getXY(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#f7c935"; ctx.lineWidth = 2; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return; e.preventDefault();
    const { x, y } = getXY(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y); ctx.stroke();
  };
  const end = () => setDrawing(false);
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  const submit = async () => {
    if (!hasDrawn) { toast.error(tr("lms.veuillez_signer_169f", "Veuillez signer")); return; }
    try {
      const b64 = canvasRef.current.toDataURL("image/png");
      await api.post(`/lms/qualiopi/documents/${doc.document_id}/sign`, {
        signature_b64: b64,
        role: "stagiaire",
        enrollment_id: enrollmentId,
      });
      toast.success(tr("lms.document_signe_b61f", "Document signe"));
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl bg-[#0b0811] border border-white/10 text-white" data-testid="signature-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-[#f7c935]" /> {doc.doc_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-white text-black rounded p-4 max-h-[40vh] overflow-y-auto text-xs whitespace-pre-wrap font-serif">{doc.content}</div>
          <div>
            <div className="text-xs text-slate-400 mb-1">{tr("lms.signez_dans_la_zone_e71e", "Signez dans la zone ci-dessous :")}</div>
            <canvas
              ref={canvasRef}
              width={600}
              height={180}
              className="bg-white rounded w-full border-2 border-dashed border-[#f7c935]/40 cursor-crosshair touch-none"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              data-testid="signature-canvas"
            />
            <div className="flex justify-between mt-2">
              <Button size="sm" variant="ghost" onClick={clear}>{tr("lms.effacer_1e9a", "Effacer")}</Button>
              <div className="text-[10px] text-slate-500">{tr("lms.horodate_ip_enregistres_7139", "Horodate + IP enregistres")}</div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="ghost" onClick={onClose}>{tr("lms.plus_tard_4fa7", "Plus tard")}</Button>
          <Button onClick={submit} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="submit-signature-btn">
            <FileSignature className="w-4 h-4 mr-2" /> {tr("lms.signer_d197", "Signer")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
